import { Stack, Grid2 as Grid, Button } from "@mui/material";
import React, { useContext, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSupplier } from "../../hooks/useSupplier";
import { PageContext } from "../../context/PageContext";
import LoadingPage from "../../components/LoadingPage";
import { Payments } from "./Payments";
import { useReadingEmail } from "../../hooks/useReadingEmail";
import { useContacts } from "../../hooks/useContacts";
import { ContactAlert } from "../../components/ContactAlert";
import { Footer } from "../../components/Footer";
import { useRedirectToActivities } from "../../hooks/useRedirectToActivities";
import { ContactNotFound } from "./ContactNotFound";
import { ContactInfo } from "./ContactInfo";
import ActionWithAi from "../../components/ActionWithAi";
import { useTasksEmail } from "../../hooks/useTasksEmail";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignButton from "../../components/AssignButton";
import { InvoiceTotalInfo } from "../../components/InvoiceTotalInfo";
import { usePayablesBySupplierOrId } from "../../hooks/usePayablesBySupplierOrId";
import { InvoiceStatus } from "../../models/ChasableModels";
import { InvoiceSelect } from "../Chasables/InvoiceSelect";
import { useTaskpaneTelemetry } from "../../telemetry/useTaskpaneTelemetry";

const Payables = () => {
  const { trackPayableSupplierListViewed, trackPayableInvoiceRowOpened } = useTaskpaneTelemetry();
  const { tasks } = useTasksEmail();
  const [searchParams] = useSearchParams();
  const hideBackButton = !!searchParams.get("hideBackButton");
  const navigate = useNavigate();

  const onBackClick = () => {
    navigate("/contacts");
  };
  const { supplierId } = useParams<{ supplierId: string }>();
  const { emailAddress } = useReadingEmail();
  const { contacts, ...contactsResponse } = useContacts(emailAddress);
  const contact = contacts?.find(({ id }) => id === supplierId);
  const { supplier, ...supplierResponse } = useSupplier(supplierId);
  const { invoices } = usePayablesBySupplierOrId({ id: supplierId ?? "", type: "Contact" });
  const validInvoices = invoices.filter(({ status }) => {
    return status !== InvoiceStatus.DELETED && status !== InvoiceStatus.VOIDED;
  });
  const { setNewTitle } = useContext(PageContext);

  useEffect(() => {
    setNewTitle("Payables");
  }, [setNewTitle]);

  useEffect(() => {
    if (!supplierId || !supplier) return;
    trackPayableSupplierListViewed({ invoice_count: String(validInvoices.length) });
  }, [supplierId, supplier?.id, validInvoices.length, trackPayableSupplierListViewed]);

  useRedirectToActivities(emailAddress);

  if (
    contactsResponse.isLoading ||
    supplierResponse.isLoading ||
    contactsResponse.isError ||
    supplierResponse.isError
  ) {
    return <LoadingPage />;
  }

  if (!supplier) {
    return <ContactNotFound email={emailAddress ?? ""} />;
  }

  const navigateToPayableInvoice = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, invoiceId: string) => {
    event.preventDefault();
    trackPayableInvoiceRowOpened();
    navigate(`/payables/${supplierId}/${invoiceId}`);
  };

  return (
    <>
      <Stack spacing={2} pb={8}>
        <ContactInfo
          title="Supplier"
          contactLink={supplier.link}
          companyName={supplier.companyName}
          accountNumber={supplier.accountNumber}
          defaultUserId={supplier.defaultUserId}
        />
        {emailAddress && (
          <ContactAlert
            isSaved={contact?.isSaved ?? false}
            email={emailAddress}
            companyName={supplier.companyName}
            contactLink={supplier.link}
            integration={supplier.integration}
            accountNumber={supplier.accountNumber}
          />
        )}
        {supplier && (
          <InvoiceTotalInfo
            totalAmount={supplier.totalAmount}
            totalDueAmount={supplier.totalDueAmount}
            link={supplier.link}
          />
        )}
        <InvoiceSelect invoices={validInvoices} onInvoiceClick={navigateToPayableInvoice} isPayable={true} />
        <Payments payments={supplier.payments} supplierLink={supplier.link} />
      </Stack>
      <Footer>
        <Grid container justifyContent="space-between" width="100%">
          {!hideBackButton && (
            <Grid size="grow">
              <Button variant="outlined" fullWidth={true} startIcon={<ArrowBackIcon />} onClick={onBackClick}>
                Back
              </Button>
            </Grid>
          )}
          <Grid size="grow">
            <ActionWithAi actions={tasks} relatedIds={[]} />
          </Grid>
          <Grid size="grow">
            <AssignButton contactId={contact?.id} />
          </Grid>
        </Grid>
      </Footer>
    </>
  );
};

export default () => {
  return <Payables />;
};
